import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import {
  InvitationMailConfirmModal,
  type InvitationSendResult,
} from '@/features/invitations/InvitationMailConfirmModal';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import type { OfficeGrade } from '@/features/auth/api';
import { ProfileFields, toDraft, toInput, type ProfileDraft } from '@/features/auth/ProfileFields';
import { createInvitation } from '@/features/invitations/api';
import { ApiError } from '@/lib/api/http';

const GRADE_OPTIONS: { value: OfficeGrade; label: string }[] = [
  { value: 'office_member', label: '一般' },
  { value: 'office_admin', label: '管理責任者' },
];

const EMPTY_DRAFT: ProfileDraft = toDraft({
  display_name: null,
  company: null,
  position: null,
  phone: null,
  contact_email: null,
  bio: null,
});

/**
 * 事務局アカウントの招待（管理責任者のみ / D-140・D-142）。
 * 受講生（/members/new）・理事講師／サクセス担当（/staff/new）と同じく、
 * 一覧画面から専用の招待画面へ入る（招待の入口を1系統に統一する）。
 * 会員登録と同じく、招待の時点で分かっているプロフィールを入れておける。
 */
export function OfficeAccountInvitePage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.office_grade === 'office_admin' || user?.active_role === 'super_admin';

  const [email, setEmail] = useState('');
  const [officeGrade, setOfficeGrade] = useState<OfficeGrade>('office_member');
  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 送る前に内容を確認してもらう（D-153）
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendResult, setSendResult] = useState<InvitationSendResult | null>(null);

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const created = await createInvitation({
        email,
        role: 'office',
        office_grade: officeGrade,
        ...toInput(draft),
      });
      // 受諾リンクはこの応答にしか無いので、閉じる前に結果として見せる（D-153）
      setSendResult({ acceptUrl: created.accept_url, mailSent: created.mail_sent });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '招待に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader title="事務局アカウントの招待" />
      <MetaBox title="招待する相手">
        {!isAdmin && (
          <Notice kind="error" dismissible={false}>
            事務局アカウントの招待は管理責任者のみ行えます。
          </Notice>
        )}
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}

        <p className="mb-3 text-wptext-muted">
          メールアドレスと権限区分（必須）のほか、分かっている項目はここで入れておけます。
          空のままにした項目は、本人が受諾後に「アカウント設定」から入力します。
        </p>

        <div className="mb-3 flex flex-wrap gap-4">
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">メールアドレス</span>
            <input
              type="email"
              required
              disabled={!isAdmin}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-64 rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">権限区分</span>
            <select
              value={officeGrade}
              disabled={!isAdmin}
              onChange={(e) => setOfficeGrade(e.target.value as OfficeGrade)}
              className="w-64 rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
            >
              {GRADE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <ProfileFields draft={draft} onChange={setDraft} idPrefix="office-invite" />

        <div className="flex gap-2">
          <button
            type="button"
            disabled={submitting || !email || !isAdmin}
            onClick={() => setConfirmOpen(true)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '招待中…' : '招待する'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/office-accounts')}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>

      <InvitationMailConfirmModal
        open={confirmOpen}
        email={email}
        busy={submitting}
        result={sendResult}
        onSend={() => void onSubmit()}
        onCancel={() => setConfirmOpen(false)}
        onDone={() => {
          setConfirmOpen(false);
          navigate('/office-accounts');
        }}
      />
    </>
  );
}
